import { Centrifuge } from "centrifuge";
import { eventTypeToAction, flattenBatches, removeBatches, WS } from "./types";
import { Auth } from "src/redux/Auth/types";
import { groupBy } from "src/utils";
import { getUserInfo } from "src/utils/auth";
import { localNow } from "src/utils/serverClock";

/**
 * New terms were published, so this session is behind: the gateway has already
 * marked it, and the next request it makes is refused.
 *
 * Raises the notice, whose only control signs the user out. Idempotent, because
 * a reconnect can deliver the publication again and the user should not be shown
 * two of them.
 */
const handleTermsPublication = (store, ctx) => {
    const version = ctx?.data?.[0]?.version || null;
    const state = store.getState();
    if (state.Auth.termsRequired || state.Auth.termsPublished === version) {
        return;
    }
    store.dispatch({ type: Auth.TERMS_PUBLISHED, version });
};

/**
 * WebSocket middleware using Centrifugo for real-time push notifications.
 *
 * Connects to Centrifugo via centrifuge-js and dispatches events to Redux.
 * Channels are assigned server-side by the auth-gateway connect proxy from the
 * session's own cpty and role, so the client subscribes to nothing itself.
 */
const webSocketMiddleware = (scope) => {
    const SCOPE_WS_CONNECT = `${WS.CONNECT}.${scope}`;
    const SCOPE_WS_DISCONNECT = `${WS.DISCONNECT}.${scope}`;

    let centrifuge = null;
    let connectInitiated = false;

    // Self-healing reconnect. The centrifugo ticket is short-lived and fetched
    // once per setup, and setup can fail outright if the backend is momentarily
    // down (e.g. nginx/auth-gateway rolling during a deploy). Centrifuge auto-
    // reconnects transient WS drops itself, but it cannot recover from a failed
    // initial fetch or a terminal disconnect (expired ticket) -- those need a
    // fresh ticket. Retry with capped backoff until connected, guarded by
    // shouldConnect so logout/disconnect stops it.
    let shouldConnect = false;
    let retryTimer = null;
    let retryDelay = 0;
    let lastConfigUrl = null;
    const RETRY_BASE_MS = 2000;
    const RETRY_MAX_MS = 30000;

    const clearRetry = () => {
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
        retryDelay = 0;
    };

    const scheduleReconnect = (store) => {
        if (!shouldConnect || retryTimer || !lastConfigUrl) return;
        retryDelay = retryDelay
            ? Math.min(retryDelay * 2, RETRY_MAX_MS)
            : RETRY_BASE_MS;
        retryTimer = setTimeout(() => {
            retryTimer = null;
            if (shouldConnect) setupCentrifugo(store, lastConfigUrl);
        }, retryDelay);
    };

    // Handle trade activity updates from server-side subscriptions
    // (trade:{cptyCode} for auditors, trade:_platform for supervisors)
    let tradeRefreshTimer = null;
    const TRADE_REFRESH_DEBOUNCE_MS = 3000; // debounce dashboard refresh

    const handleTradePublication = (store, ctx) => {
        // Debounce: if multiple trade updates arrive quickly (e.g. supervisor
        // receiving updates from many cpties), only refresh once after they settle
        if (tradeRefreshTimer) clearTimeout(tradeRefreshTimer);
        tradeRefreshTimer = setTimeout(() => {
            tradeRefreshTimer = null;
            try {
                const { DashboardActions } = require("src/redux/Dashboard/actions");
                store.dispatch(DashboardActions.FetchAll());
            } catch (e) {
                console.error("ws: Failed to refresh dashboard on trade update", e);
            }
        }, ctx.channel === "trade:_platform" ? TRADE_REFRESH_DEBOUNCE_MS : 500);
    };

    const handlePublication = (store) => (ctx) => {
        try {
            const eventObjs = ctx.data;
            if (!eventObjs || !eventObjs.body) {
                return;
            }

            const batchesSet = flattenBatches(eventObjs.body);
            batchesSet.forEach((e) => {
                try {
                    store.dispatch(eventTypeToAction(e.type).call(null, e));
                } catch (exc) {
                    (console.error || console.log).call(
                        console,
                        exc.stack || exc
                    );
                }
            });

            const dataSet = removeBatches(eventObjs.body);
            const groupedByEventType = groupBy(dataSet, "type");

            Object.keys(groupedByEventType).forEach((t) => {
                let event = {
                    type: t,
                    txEntries: groupedByEventType[t],
                };
                try {
                    store.dispatch(eventTypeToAction(t).call(null, event));
                } catch (exc) {
                    (console.error || console.log).call(
                        console,
                        exc.stack || exc
                    );
                }
            });
        } catch (e) {
            return;
        }
    };

    function teardownClient() {
        // Null the ref first so any 'disconnected' event fired by disconnect()
        // sees a superseded client and does not schedule a reconnect. Server-side
        // subscriptions go away with the connection, so there is nothing else to
        // unsubscribe.
        const c = centrifuge;
        centrifuge = null;
        if (c) {
            try { c.disconnect(); } catch (e) { /* ignore */ }
        }
    }

    function setupCentrifugo(store, configUrl) {
        lastConfigUrl = configUrl;
        // Drop any prior client before minting a fresh ticket + client.
        teardownClient();
        // The WS connects with a short-lived sealed ticket minted from the
        // session cookie - the browser never holds a Keycloak token.
        fetch("/auth/centrifugo-token", { method: "POST", credentials: "include" })
            .then(async (resp) => {
                if (!resp.ok) {
                    throw new Error(`centrifugo-token request failed: ${resp.status}`);
                }
                const { token: authToken } = await resp.json();
                const userInfo = getUserInfo();
                const cptyCode = userInfo?.cptyCode;
                if (!cptyCode) {
                    console.log(scope, "ws: No cptyCode, cannot subscribe");
                    return;
                }

                // Build full WebSocket URL from the config path
                const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
                const wsUrl = `${proto}//${window.location.host}${configUrl}`;

                centrifuge = new Centrifuge(wsUrl, {
                    data: { token: authToken },
                });
                const thisClient = centrifuge;

                let wsConnectedDispatched = false;
                // WS_CONNECTED is one half of login readiness (the other is
                // Auth.INIT_COMPLETE from CompleteLogin); the reducer flips
                // appReady only when both have arrived. Dispatched from the
                // 'subscribed' event for truweb:{cpty}, not the transport
                // 'connected' event: a ready client must be able to receive
                // publications on that channel, and a publish before the
                // subscription is established is dropped (Centrifugo keeps no
                // history here).
                const dispatchWsConnected = () => {
                    if (!wsConnectedDispatched) {
                        wsConnectedDispatched = true;
                        store.dispatch({ type: "auth.ws_connected" });
                    }
                };

                const connectStartedAt = localNow();

                centrifuge.on("connected", (ctx) => {
                    console.log(
                        scope,
                        `ws: Centrifugo connected in ${localNow() - connectStartedAt}ms.`,
                        ctx
                    );
                    connectInitiated = false;
                    clearRetry();
                });

                centrifuge.on("disconnected", (ctx) => {
                    console.log(scope, "ws: Centrifugo disconnected.", ctx);
                    // Reaching 'disconnected' means centrifuge stopped its own
                    // reconnect loop (e.g. a terminal disconnect from an expired
                    // ticket). Re-establish with a fresh ticket, unless this
                    // client was superseded/torn down or we intend to stay off.
                    if (centrifuge === thisClient && shouldConnect) {
                        centrifuge = null;
                        scheduleReconnect(store);
                    }
                });

                centrifuge.on("error", (ctx) => {
                    console.log(scope, "ws: Centrifugo error:", ctx);
                });

                const channel = "truweb:" + cptyCode.toLowerCase();
                const onPublication = handlePublication(store);

                // Every channel this connection receives is a server-side
                // subscription assigned by the connect proxy from the session's
                // own cpty and role. Clients cannot subscribe themselves, so a
                // channel arriving here is already authorized; route it by
                // prefix to the handler that understands its payload.
                centrifuge.on("publication", (ctx) => {
                    console.log(scope, "ws: Server-side publication on", ctx.channel);
                    if (!ctx.channel) {
                        return;
                    }
                    if (ctx.channel === "platform:terms") {
                        handleTermsPublication(store, ctx);
                    } else if (ctx.channel.startsWith("trade:")) {
                        handleTradePublication(store, ctx);
                    } else if (ctx.channel.startsWith("truweb:")) {
                        onPublication(ctx);
                    }
                });

                centrifuge.on("subscribing", (ctx) => {
                    console.log(scope, "ws: Subscribing to", ctx.channel, ctx);
                });

                centrifuge.on("subscribed", (ctx) => {
                    console.log(
                        scope,
                        `ws: Subscribed to ${ctx.channel} in ${localNow() - connectStartedAt}ms`
                    );
                    if (ctx.channel === channel) {
                        dispatchWsConnected();
                    }
                });

                centrifuge.connect();
                console.log(scope, "ws: Centrifugo client set up for channel", channel);
            })
            .catch((reason) => {
                // Setup failed before a client existed (e.g. the ticket fetch
                // 401'd while the ingress was rolling during a deploy). Nothing
                // is auto-reconnecting, so retry with backoff until the backend
                // is reachable again -- otherwise the WS stays dead until a
                // manual page refresh.
                console.log("ws: Failed to setup Centrifugo", reason);
                connectInitiated = false;
                scheduleReconnect(store);
            });
    }

    function disconnect() {
        // Intentional stop (logout / explicit disconnect): stop retrying first
        // so the client's 'disconnected' event does not schedule a reconnect.
        shouldConnect = false;
        clearRetry();
        teardownClient();
        connectInitiated = false;
    }

    return (store) => (next) => (action) => {
        if (action === undefined) {
            return next(action);
        }

        switch (action.type) {
            case SCOPE_WS_CONNECT:
                if (connectInitiated) {
                    console.log(
                        scope,
                        "ws: Workaround: Connection already initiated, returning early.."
                    );
                    break;
                }

                console.log(scope, "ws: SCOPE_WS_CONNECT");
                disconnect();

                if (!action.url) {
                    break;
                }

                // Arm self-healing reconnect for this session (disconnect()
                // above cleared it).
                shouldConnect = true;
                connectInitiated = true;
                setupCentrifugo(store, action.url);
                break;

            case Auth.LOGOUT:
            case SCOPE_WS_DISCONNECT:
                disconnect();
                console.log(scope, "ws: Centrifugo disconnected");
                break;

            default:
                break;
        }

        return next(action);
    };
};

export default webSocketMiddleware;
